import type { FC } from 'react';
import { useContext, useMemo } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { Url, USER_PROFILE_SECTION_PATHS } from '@wix/da-url';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ThumbStatusIndicator from '@wix/da-ds/pkg/thumbs/ThumbStatusIndicator';
import ChevronRightIcon from '@wix/da-ds/pkg/Icons/16x16/ChevronRight';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import { useTranslation } from 'react-i18next';
import { getProfileOwnerUser } from '../../../../selectors/users';

import s from './GalleryTierUpsellCard.scss';

export interface Props {
  tierCount: number;
  promoDeviation?: PapiDeviation;
  className?: string;
}

const GalleryTierUpsellCard: FC<Props> = ({
  tierCount,
  promoDeviation,
  className,
}) => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  const profileOwner = useSelector(getProfileOwnerUser);
  const href = Url.userLink(
    profileOwner?.username,
    USER_PROFILE_SECTION_PATHS.SUBSCRIPTIONS
  );

  const coverSrc = useMemo(() => {
    if (!promoDeviation) {
      return undefined;
    }
    return DeviationMediaService.getSmallestThumb(promoDeviation)?.src;
  }, [promoDeviation]);

  if (isMobile) {
    return (
      <a href={href} className={classnames(s['root'], s['mobile'], className)}>
        <div className={s['mobile-cover']}>
          {coverSrc ? (
            <img alt="" className={s['cover-image']} src={coverSrc} />
          ) : (
            <div className={s['cover-placeholder']} />
          )}
        </div>
        <div className={s['mobile-content']}>
          <ThumbStatusIndicator type="in-tier" hasAccess size="medium" />
          <div className={s['mobile-text']}>
            <span className={s['title']}>
              {t('pages.gallections.tierUpsell.title')}
            </span>
            <span className={s['subtitle']}>
              {t('pages.gallections.tierUpsell.subtitle', {
                count: tierCount,
              })}
            </span>
          </div>
        </div>
        <div className={s['mobile-chevron']}>
          <ChevronRightIcon size={IconSize.SMALLER} />
        </div>
      </a>
    );
  }

  return (
    <a href={href} className={classnames(s['root'], className)}>
      <div className={s['cover']}>
        {coverSrc ? (
          <img alt="" className={s['cover-image']} src={coverSrc} />
        ) : (
          <div className={s['cover-placeholder']} />
        )}
      </div>
      <div className={s['info']}>
        <span className={s['title']}>
          {t('pages.gallections.tierUpsell.title')}
        </span>
        <span className={s['subtitle']}>
          {t('pages.gallections.tierUpsell.subtitle', { count: tierCount })}
        </span>
      </div>
      <div className={s['indicator']}>
        <ThumbStatusIndicator type="in-tier" hasAccess size="large" />
      </div>
    </a>
  );
};

GalleryTierUpsellCard.displayName = 'GalleryTierUpsellCard';
export default GalleryTierUpsellCard;
